// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {AfterViewInit, Directive, Input, OnDestroy} from '@angular/core';
import {EventsService} from 'src/services/events-service';
import {EVENT_TYPES} from 'src/types/events';
import AuthHelper from 'src/app/auth/authHelper';
import { NotificationService } from '../../../services/notification-service';
import { AuthService } from '../../../services/auth-service';

@Directive({
    selector: '[appLifecycleTracker]',
    standalone: false
})
export class LifecycleTrackerDirective implements AfterViewInit, OnDestroy {
  @Input() trackerName: string;

  private startTime = 0;
  private endTime = 0;
  readonly currentUser: string = '';
  readonly currentUserId: number = -1;
  readonly tokenId: string = '';

  constructor(private eventsService: EventsService,
              private notificationService: NotificationService,
              private authService: AuthService) {
    this.currentUser = AuthHelper.getUserFromToken();
    this.currentUserId = AuthHelper.getUidFromToken();
    this.tokenId = AuthHelper.getTokenIdFromToken();
  }

  ngAfterViewInit(): void {
    this.startTracking();
  }

  ngOnDestroy(): void {
    this.notificationService.dismiss();
    this.endTracking();
  }

  startTracking(): void {
    const date = new Date();
    this.startTime = this.getUTCDate(date);
    this.sendEventsInfo(date, false);
  }

  endTracking(): void {
    if (this.authService.isLoggedIn) {
      const date = new Date();
      this.endTime = this.getUTCDate(date);
      this.sendEventsInfo(date, true);
    }
  }

  getUTCDate(date: Date): number {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(),
        date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
  }

  sendEventsInfo(date: Date, isComplete: boolean): void {
    let  description: string;
    let detail: string;

    if (isComplete) {
      const timeSpent = this.endTime - this.startTime;
      const timeSpentOnPage = this.getInterval(timeSpent);
      description = `${this.trackerName} closed`;
      detail = `User (session=${this.tokenId} uid=${this.currentUserId}) ${this.currentUser} closed 
            ${this.trackerName} at ${date.toISOString()} with a time on page of ${timeSpentOnPage}. (${timeSpent}ms)`;
    } else {
      description = `${this.trackerName} opened`;
      detail = `User (session=${this.tokenId} uid=${this.currentUserId}) ${this.currentUser} opened 
            ${this.trackerName} at ${date.toISOString()}`;
    }

    const type = EVENT_TYPES.NAVIGATION;
    this.eventsService.debug({type, description, detail}).subscribe();
  }
  
  getInterval(interval: number): string {
    let unit = '';
    if (interval >= 0 && interval < 1000) {
      unit = 'millisecond';
    } else if (interval >= 1000 && interval < 60000) {
      interval = interval / 1000;
      unit = 'second';
    } else if (interval >= 60000 && interval < 3600000) {
      interval = interval / 1000 / 60;
      unit = 'minute';
    } else if (interval >= 3600000 && interval < 86400000) {
      interval = interval / 1000 / 60 / 60;
      unit = 'hour';
    } else if (interval >= 86400000) {
      interval = interval / 1000 / 60 / 60 / 24;
      unit = 'day';
    }

    interval = Math.round((interval + Number.EPSILON) * 100) / 100;
    return interval < 0 || interval === 1 ? `${interval} ${unit}`.trim() : `${interval} ${unit}s`;
  }

}

